import React, { useEffect, useState } from 'react';
import Header from '../../components/common/Header/Header.jsx';
import Footer from '../../components/common/Footer/Footer.jsx';
import './CapabilitiesPage.css';

const CAPABILITY_CARDS = [
  {
    title: 'Troubleshoot Complex Issues',
    items: [
      'Accurate contextual recommendations for troubleshooting steps, probing questions, diagnostics needed and future journey predictions at every case progression',
      'Auto-send Meaningful First Response to ask probing questions, collect diagnostics and guide self-service (with or without human review)',
      'Auto-collect customer diagnostics from your product backend/diagnostic tools using APIs or MCP servers',
      'Auto-generated responses with tone, grammar, and empathy checks at every case progression',
      'Predict and auto-schedule Zoom calls, remote access sessions and lab environments',
      'Manage and automatically apply Customer-Specific Handling Instructions',
      'Detect missing/incomplete steps from case documentation and prompt Support Engineers to complete it',
      'Visual editor for manually overriding the auto-generated Troubleshooting Maps™',
      'Re-inforcement learning to auto-improve accuracy of troubleshooting steps, by comparing actual troubleshooting with recommendations at each step'
    ]
  },
  {
    title: 'Maximize Self-Service',
    items: [
      <React.Fragment key="kai">
        <a
          href="https://videopress.com/v/zKA5efTw"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'underline', color: 'inherit' }}
          onClick={(e) => e.stopPropagation()}
        >
          Kai
        </a> - the full-service AI Support Engineer that actively troubleshoots with your customers to self-service every issue in which every step can be performed by the customer
      </React.Fragment>,
      'Confidence-based auto-resolution of a step, or the entire case, if the steps can be performed by the customer',
      'In-product Contextual Support',
      'Auto Respond to collect diagnostics with multi-turn conversations',
      'Evaluate self-serviceability at every case progression',
      'Automated escalation to Human when AI confidence is low, or the next action requires Support Engineer execution',
      'Execute safe backend data/configuration updates',
      'In-product, Slack, WhatsApp, SMS, Chat and voice-based interactivity for customers'
    ]
  },
  {
    title: 'Manage Ticket Lifecycle',
    items: [
      'Case complexity and trajectory (zoom, remote access, lab environment, diagnostic needs, skills and tools, etc.) prediction at every case progression',
      'Queue Management with rebalancing',
      'Backlog Management',
      'Predict and auto-advance case to next tier of support',
      'Auto-scan every open issue as new troubleshooting paths become viable',
      'Automated follow-ups for stale and waiting-for-information cases',
      'Auto-close resolved cases with 3-strike follow-ups for customer confirmation',
      'Auto-fetch updates for cases advanced to Engineering or Cloud Ops',
      'Detect and unstick stalled or aging tickets'
    ]
  },
  {
    title: 'Supercharge Support Engineers',
    items: [
      'Case-contextual education auto-generated and presented to the Support Engineers within the case workflow',
      'Initial case assignment, and any re-assignment based on predicted complexity and auto-learned Support Engineers’ experience with similar issues',
      'Detect and auto-correct spellings, grammar, tone and empathy in every outgoing message',
      'Detect and alert PII, sensitive information and risky operation suggestions in every outgoing message',
      'Skill gap identification, training recommendations and performance feedback',
      'Recommend contextually experienced Support Engineers to ask for assistance',
      'Support Engineer performance insights',
      'Suggested and translated customer responses',
      'Cursor-like predictive experience for Support Engineers with integrated product diagnostics'
    ]
  },
  {
    title: 'Hyper-Care',
    items: [
      'Hyper-Care for New Customers',
      'Hyper-Care for New Product Releases',
      'Hyper-Care for New Products',
      'Detect regressions, and release-related ticket spikes in real time',
      'Critical Accounts Program',
      'At-risk customer management',
      'Multi-player mode and swarming'
    ]
  },
  {
    title: 'Optimize Resources',
    items: [
      'Hiring plan recommendations',
      'What-if scenario analysis for capacity vs SLA',
      'Duty Charts to maximize SLAs and coverage',
      'Case handoff recommendations, with auto-generated hand-off summaries',
      'Lab equipment management including version-aware predictive assignment'
    ]
  },
  {
    title: 'Global Ecosystem',
    items: [
      'Troubleshooting intelligence for your Service and reseller partners via APIs and UI',
      'Troubleshooting Maps for common base technologies included out of the box',
      'Include Troubleshooting Maps from your product ecosystem partners',
      'Flexible in-network, DMZ and in-the-cloud deployment options based on your ecosystem of customers and partners'
    ]
  },
  {
    title: 'Strategic Intelligence',
    items: [
      'Surface recurring product enhancement needs and configuration patterns',
      'Highlight recurring manual actions that are candidates for automation',
      'Predict and highlight at-risk and disengaged customers',
      'Renewal risk predictor',
      'Upsell opportunities identifier',
      'Real-time customer sentiment measurement and recommended corrective actions',
      'Real-time escalation likelihood prediction',
      'Customer prioritization and Critical Account Management insights',
      'Alerts for known fixes, patches, and risky operations',
      'Auto-assess CSAT based on customer tonality, resolution confirmation and comparable resolution times'
    ]
  }
];

const CapabilitiesPage = () => {
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  const handleCardClick = (index) => {
    setActiveIndex((prev) => (prev === index ? -1 : index));
  };

  const renderCard = (card, index) => {
    const isActive = index === activeIndex;
    return (
      <div
        key={card.title}
        role="button"
        tabIndex={0}
        className={`capabilities-card${isActive ? ' capabilities-card--active' : ''}`}
        onClick={() => handleCardClick(index)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick(index);
          }
        }}
        aria-expanded={isActive}
      >
        <div className="capabilities-card-header">
          <span className="capabilities-card-label">{card.title}</span>
          <span className="capabilities-card-icon" aria-hidden="true">
            {isActive ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M10.9375 3.0625L3.0625 10.9375M10.9375 10.9375L3.0625 3.0625"
                  stroke="#FAFAFA"
                  strokeWidth="1.31"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M2.1875 7H11.8125M7 2.1875V11.8125"
                  stroke="#FAFAFA"
                  strokeWidth="1.31"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </span>
        </div>
        <div className="capabilities-card-body" aria-hidden={!isActive}>
          <ul className="capabilities-card-list">
            {card.items.map((item, idx) => (
              <li key={typeof item === 'string' ? item : idx}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <div className="capabilities-page">
      <Header />

      <main className="capabilities-page__main">
        {/* Hero */}
        <section className="capabilities-hero">
          <div className="capabilities-hero-bg" aria-hidden="true" />
          <div className="capabilities-hero-inner">
            <div className="capabilities-grid-header">
              <h2 className="capabilities-section-heading">Kahuna AI Capabilities</h2>
            </div>
            <h1 className="capabilities-title">
              <span className="capabilities-title-desktop">
                <span className="capabilities-title-line">One Comprehensive AI Platform </span>
                <span className="capabilities-title-line">for Technical Support</span>
              </span>
              <span className="capabilities-title-tablet">
                <span className="capabilities-title-line">One Comprehensive </span>
                <span className="capabilities-title-line">AI Platform for </span>
                <span className="capabilities-title-line">Technical Support</span>
              </span>
              <span className="capabilities-title-mobile">
                <span className="capabilities-title-line">One </span>
                <span className="capabilities-title-line">Comprehensive </span>
                <span className="capabilities-title-line">AI Platform</span>
                <span className="capabilities-title-line">for Technical Support</span>
              </span>
            </h1>
            <p className="capabilities-subtitle">
              <span className="capabilities-subtitle-desktop">
                <span className="capabilities-subtitle-line">Everything you need, reimagined with AI-first principles </span>
              </span>
              <span className="capabilities-subtitle-mobile">
                <span className="capabilities-subtitle-line">Everything you need, reimagined  </span>
                <span className="capabilities-subtitle-line">with AI-first principles </span>
              </span>
            </p>
            <div className="capabilities-brand-lockup">
              <img
                src="/logos/Logo-color.svg"
                alt=""
                className="capabilities-brand-mark"
              />
              <span className="capabilities-brand-name">Kahuna AI</span>
            </div>
          </div>
        </section>

        {/* Capability grid - two independent columns */}
        <section className="capabilities-grid-section">
          <div className="capabilities-grid" role="tablist" aria-label="Kahuna capabilities">
            <div className="capabilities-grid-column">
              {CAPABILITY_CARDS.slice(0, 4).map((card, index) =>
                renderCard(card, index)
              )}
            </div>
            <div className="capabilities-grid-column">
              {CAPABILITY_CARDS.slice(4, 8).map((card, index) =>
                renderCard(card, index + 4)
              )}
            </div>
          </div>
        </section>

        {/* To get started — centered pill CTA */}
        <section className="capabilities-cta-section">
          <div className="capabilities-cta-bg" aria-hidden="true" />
          <div className="capabilities-cta-inner">
            <div className="capabilities-cta-pill">
              <span className="capabilities-cta-pill-label">To get started</span>
              <a
                href="https://form.jotform.com/251278392049160"
                className="header-cta-button"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>Estimate Your Troubleshooting Map</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M3.65479 10.5002L10.6548 3.50024M10.6548 3.50024H4.96729M10.6548 3.50024V9.18774" stroke="#fff" stroke-width="1.31" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default CapabilitiesPage;
